// eslint-disable-next-line import/default
import Bowser from 'bowser';
import { differenceInMilliseconds, parseISO } from 'date-fns';
import { TFunction } from 'i18next';
import { PrimitiveAtom, atom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { parse, stringify } from 'qs';
import { debounce as baseDebounce, mergeDeep, pipe } from 'remeda';
import { twMerge } from 'tailwind-merge';
import { v4 as uuid4 } from 'uuid';

import { isMobile } from '@growtherapy/analytics';

import { JotaiStore } from './state/types';
import { Browser, DeviceType } from './types';
import { isZoomExecutedFailure, toZoomExecutedFailureError } from './video-sdk/zoom/utils/error';

export function getCurrentHost() {
  if (typeof window === 'undefined') return '';

  return window.location.hostname;
}

export function callAndDelay(callback: () => unknown, delayMs: number) {
  callback();

  return new Promise((resolve) => {
    setTimeout(resolve, delayMs);
  });
}

export function parseQueryParams<T extends Record<string, unknown>>(
  /**
   * Query string including the "?" prefix e.g. `location.search`
   */
  queryString: string,
): Partial<T> {
  return parse(queryString, { ignoreQueryPrefix: true }) as Partial<T>;
}

export function setQueryParams(url: string, params: Record<string, string>): string {
  const givenUrlParams = parseQueryParams(new URL(url).search);
  const updatedUrl = new URL(url);
  updatedUrl.search = stringify({ ...givenUrlParams, ...params });
  return updatedUrl.href;
}

export function noop<T>(..._args: unknown[]) {
  return undefined as unknown as T;
}

export function nameToInitials(name: string) {
  const alphaNumericChars = name.replace(/[^ a-zA-Z0-9]/g, '');
  const nameParts = alphaNumericChars.split(' ').filter((w) => w.length > 0);
  const numInitialsToInclude = nameParts.length === 2 ? 2 : 1;
  const shortenedNameParts = nameParts.splice(0, numInitialsToInclude);

  return shortenedNameParts
    .map((w) => w.charAt(0))
    .join('')
    .toUpperCase();
}

export function nameToInitialsWithPeriods(firstName?: string | null, lastName?: string | null) {
  if (firstName && lastName) {
    return `${firstName[0]}.${lastName[0]}.`;
  }
  return '';
}
export function olderThan(timestamp: string, millis: number) {
  return differenceInMilliseconds(new Date(), parseISO(timestamp)) >= millis;
}

// eslint-disable-next-line @typescript-eslint/ban-types
export function isFunction(value: unknown): value is Function {
  return typeof value === 'function';
}

/**
 * Returns a short unique ID i.e. short ID. Accepts an optional UUID generator
 * to override {@link uuid4}.
 */
export function shortUuid(uuidGenerator?: () => string) {
  return (uuidGenerator ? uuidGenerator() : uuid4()).slice(0, 8);
}

/**
 * Debounce a function, ensuring that it is only called at most once within the
 * given time. Drops calls that occur during the cooldown.
 */
export function debounce<FnParams extends unknown[], FnReturn = unknown>(
  fn: (...args: FnParams) => FnReturn,
  waitMs: number,
  timing: 'both' | 'leading' | 'trailing' = 'leading',
): (...funcArgs: FnParams) => FnReturn | undefined {
  const debouncer = baseDebounce(fn, {
    waitMs,
    // Typings are wack here; it accepts "trailing" but not when in the union
    timing: timing as 'both' | 'leading',
  });

  return (...args: FnParams) => {
    // This prevents the cooldown from being extended everytime a call is made
    if (timing === 'leading' && debouncer.isPending) return;
    return debouncer.call(...args);
  };
}

export function capitalize(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function getDeviceType() {
  return isMobile(navigator.userAgent) ? DeviceType.Mobile : DeviceType.Desktop;
}

export function isScreenShareSupported(): boolean {
  return typeof navigator?.mediaDevices?.getDisplayMedia === 'function';
}

export function deviceSupportsViewTransitions() {
  return (
    'startViewTransition' in document &&
    !isMobile(navigator.userAgent) &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export function deviceIsDesktopChrome() {
  return /Chrome/.test(navigator.userAgent);
}

export function isSafari() {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}

export function addImmutablyToSet<T>(set: Set<T>, value: T) {
  return new Set([...set, value]);
}

export function removeImmutablyFromSet<T>(set: Set<T>, value: T) {
  const newSet = new Set(set);
  newSet.delete(value);
  return newSet;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function memoize<T extends (...args: any[]) => any>(fn: T): T {
  const cache: { [key: string]: ReturnType<T> } = {};

  return ((...args: Parameters<T>): ReturnType<T> => {
    const key = JSON.stringify(args);
    if (key in cache) {
      return cache[key];
    } else {
      const result = fn(...args);
      cache[key] = result;
      return result;
    }
  }) as T;
}

export function memoizedDeserializerWithInitialValue<T>(
  initialValue: T | (() => T),
  // calling methods should handle the error (to avoid a circular dependency)
  onParseError?: (error: Error) => void,
) {
  // returns a memoized version of the deserializer defined in useLocalStorage:
  // https://github.com/juliencrn/usehooks-ts/blob/master/packages/usehooks-ts/src/useLocalStorage/useLocalStorage.ts#L71-L87
  return memoize((value: string): T => {
    const defaultValue = initialValue instanceof Function ? initialValue() : initialValue;

    // this is a break from the original source, which returns undefined cast as
    // T. IMO, this is a less error-prone default behavior.
    if (value === 'undefined') {
      return defaultValue;
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(value);
    } catch (error) {
      onParseError?.(error as Error);
      return defaultValue;
    }

    return parsed as T;
  });
}

export function isElementVisible(element: HTMLElement) {
  const style = window.getComputedStyle(element);
  return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
}

export function isDocumentVisible() {
  return document?.visibilityState === 'visible';
}

export function gracefullyParseJson<T>(json?: string | null): T | null {
  if (!json) return null;
  try {
    return JSON.parse(json ?? '{}');
  } catch {
    return null;
  }
}

export function gracefullyStringifyJson(obj: object): string | null {
  try {
    return JSON.stringify(obj);
  } catch {
    return null;
  }
}

/**
 * Returns true if the value is in the list, otherwise false. If the list is
 * undefined, the value is always allowed. If `isDenyList` is true, the value is
 * allowed if it is not in the list.
 */
export function checkIsAllowed<T>(
  value: T,
  {
    allowList,
    denyList,
  }: {
    allowList?: T[];
    denyList?: T[];
  } = {},
) {
  if (!allowList && !denyList) return true;

  const isAllowed = !allowList || allowList.includes(value);
  const isDenied = denyList && denyList.includes(value);
  return isAllowed && !isDenied;
}

export const detectBrowser = memoize((): Browser => {
  // eslint-disable-next-line import/no-named-as-default-member
  const browser = Bowser.getParser(window.navigator.userAgent);
  const browserName = browser.getBrowserName().toLowerCase();

  if (browserName.includes(Browser.Chrome)) {
    return Browser.Chrome;
  }

  if (browserName.includes(Browser.Safari)) {
    return Browser.Safari;
  }

  if (browserName.includes(Browser.Firefox)) {
    return Browser.Firefox;
  }

  if (browserName.includes(Browser.Edge)) {
    return Browser.Edge;
  }

  if (browserName.includes(Browser.Opera)) {
    return Browser.Opera;
  }

  return Browser.Unknown;
});

// Zoom specific concern for Desktop Safari running macOS 15.2 to 16.0.
export function isDesktopSafari15_2To16(): boolean {
  // eslint-disable-next-line import/no-named-as-default-member
  const parser = Bowser.getParser(window.navigator.userAgent);
  return parser.getPlatformType() === 'desktop' && parser.satisfies({ safari: '>=15.2 <=16' }) === true;
}

// Common hashing function, similar to the one here: https://stackoverflow.com/questions/7616461/generate-a-hash-from-string-in-javascript
export function hashString(str: string) {
  let hash = 0;
  if (str.length === 0) return hash;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return hash;
}

export function hashObject(obj: object) {
  return hashString(JSON.stringify(obj));
}

// a function to shuffle an array with a seed, such that given the same array
// with the same seed, you will get the same shuffled array
export function seededShuffle<T>(source: T[], shuffleSeed: number): T[] {
  // we modify the array in place, so we make a copy to avoid modifying the original
  const output = [...source];
  let m = output.length;
  let t: T;
  let i: number;

  // ensure seed is positive to avoid issues with modulo putting elements at negative indices
  let seed = shuffleSeed < 0 ? -1 * shuffleSeed : shuffleSeed;

  while (m) {
    i = Math.floor(seed % m--);
    t = output[m];
    output[m] = output[i];
    output[i] = t;
    seed = Math.floor(seed / m);
  }

  return output;
}

export function asTimeWithTimeZone(time?: string) {
  if (!time) return '';

  return new Date(time).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    timeZoneName: 'short',
  });
}

/**
 * Maps over the array with the given function and then filters out
 * null/undefined values. Note, this means other falsey return values will be
 * included.
 */
export function keep<T, U>(arr: Array<T>, fn: (val: T, index: number) => U | null | undefined) {
  return arr.map(fn).filter((val) => val !== null && val !== undefined) as U[];
}

export function createDerivedWritableAtom<Type, Key extends keyof Type>(
  base: PrimitiveAtom<Type>,
  key: Key,
  options?: {
    defaultGetValue: NonNullable<Type[Key]>;
    debugLabel?: string;
  },
): PrimitiveAtom<Type[Key]> {
  const derivedAtom = atom(
    // I do not typically like type assertion, especially where it could be
    // undefined, but the signature of the method should ensure that the value
    // cannot be undefined/null
    (get) => get(base)[key] ?? (options?.defaultGetValue as Type[Key]),
    (_get, set, valueOrFunction) => {
      set(base, (prev) => ({
        ...prev,
        [key]: isFunction(valueOrFunction) ? valueOrFunction(prev[key]) : valueOrFunction,
      }));
    },
  );

  const baseDebugLabelPrefix = base?.debugLabel ? `${base.debugLabel}.` : '';
  derivedAtom.debugLabel = options?.debugLabel ?? `${baseDebugLabelPrefix}${String(key)}`;

  return derivedAtom;
}

export function createDerivedWritableAtomFamily<Type, Param extends keyof Type, Key extends keyof Type[Param]>(
  base: PrimitiveAtom<Type>,
  key: Key,
  options?: {
    defaultGetValue: NonNullable<Type[Param][Key]>;
  },
) {
  return atomFamily((param: Param) =>
    atom(
      // I do not typically like type assertion, especially where it could be
      // undefined, but the signature of the method should ensure that the value
      // cannot be undefined/null
      (get) => get(base)[param]?.[key] ?? (options?.defaultGetValue as Type[Param][Key]),
      (_get, set, valueOrFunction) => {
        set(base, (prev) => ({
          ...prev,
          [param]: {
            ...prev[param],
            [key]: isFunction(valueOrFunction) ? valueOrFunction(prev[param][key]) : valueOrFunction,
          },
        }));
      },
    ),
  );
}

export const pluralizeString = (count: number, singular: string) => (count === 1 ? singular : `${singular}s`);

/**
 * Convert a number (0-10) to its word representation using i18n.
 * For numbers outside this range, returns the number as a string.
 *
 * @param num - The number to convert (0-10)
 * @param t - The i18next translation function
 * @returns The word representation (e.g., "one", "two") or the number as a string for strings > 10 or < 0
 *
 * @example
 * numberToWord(3, t); // "three"
 * numberToWord(15, t); // "15"
 */
export function numberToWord(num: number, t: TFunction): string {
  switch (num) {
    case 0:
      return t('telehealth-core:numbers.zero');
    case 1:
      return t('telehealth-core:numbers.one');
    case 2:
      return t('telehealth-core:numbers.two');
    case 3:
      return t('telehealth-core:numbers.three');
    case 4:
      return t('telehealth-core:numbers.four');
    case 5:
      return t('telehealth-core:numbers.five');
    case 6:
      return t('telehealth-core:numbers.six');
    case 7:
      return t('telehealth-core:numbers.seven');
    case 8:
      return t('telehealth-core:numbers.eight');
    case 9:
      return t('telehealth-core:numbers.nine');
    case 10:
      return t('telehealth-core:numbers.ten');
    default:
      return num.toString();
  }
}

/**
 * Converts a decimal to the simplest fraction that reproduces it, returned as a
 * `[numerator, denominator]` tuple.
 *
 * Uses a continued-fraction expansion, walking its convergents until one
 * exactly reproduces the input or the next one would exceed
 * `maxDenominator`. The cap keeps imprecise inputs (e.g. a rounded 1.78) from
 * producing unwieldy fractions, at the cost of returning an approximation.
 *
 * @param decimal - The (finite) number to convert. Negatives are supported; the
 * sign is carried by the numerator.
 * @param maxDenominator - The largest denominator to allow. Defaults to 100.
 *
 * @example
 * decimalToFraction(16 / 9); // [16, 9]
 * decimalToFraction(0.5); // [1, 2]
 * decimalToFraction(Math.PI); // [22, 7]
 */
export function decimalToFraction(
  decimal: number,
  { maxDenominator = 100 }: { maxDenominator?: number } = {},
): [number, number] {
  if (!Number.isFinite(decimal)) {
    throw new RangeError(`decimalToFraction expects a finite number, received ${decimal}`);
  }

  const sign = decimal < 0 ? -1 : 1;
  const target = Math.abs(decimal);

  // The two most recent convergents, oldest first
  let [previousNumerator, numerator] = [0, 1];
  let [previousDenominator, denominator] = [1, 0];
  let remaining = target;

  for (;;) {
    const term = Math.floor(remaining);
    const nextNumerator = term * numerator + previousNumerator;
    const nextDenominator = term * denominator + previousDenominator;

    if (nextDenominator > maxDenominator) break;

    [previousNumerator, numerator] = [numerator, nextNumerator];
    [previousDenominator, denominator] = [denominator, nextDenominator];

    const remainder = remaining - term;
    // The expansion terminated, so this convergent is exact
    if (remainder === 0) break;
    // Floating point error can leave the convergent exact before the expansion
    // terminates, in which case further terms only add noise
    if (numerator / denominator === target) break;

    remaining = 1 / remainder;
  }

  return [sign * numerator, denominator];
}

/**
 * Filters out null and undefined values from an array.
 */
export function filterNonNullable<T>(array: Array<T>): NonNullable<T>[] {
  return array.filter((item) => item !== null && item !== undefined);
}

export function arrayEquals<T>(a?: T[], b?: T[]) {
  // this is effectively XOR, but ^ confuses the linter!
  if (a === b) return true;
  if (!a || !b) return false;

  if (a?.length !== b?.length) return false;

  return a.every((v, i) => b[i] === v);
}

type MergableProps = {
  className?: string;
  [key: string]: unknown;
};

export function mergeProps<Source extends MergableProps, Destination extends MergableProps>(
  source: Source,
  destination: Destination,
): Source & Destination {
  const { className: sourceClassName } = source;
  const { className: destinationClassName } = destination;

  const classNameProp =
    sourceClassName && destinationClassName ? { className: twMerge(sourceClassName, destinationClassName) } : {};

  return pipe(
    source,
    (source) => mergeDeep(source, destination),
    (merged) => ({ ...merged, ...classNameProp }),
  ) as Source & Destination;
}

/**
 * Merges multiple refs into a single ref callback. This is useful when you
 * want to pass multiple refs to a single component.
 * @param refs - An array of refs to merge.
 * @returns A unified ref callback.
 */
export function mergeRefs<T>(...refs: Array<React.Ref<T>>): React.RefCallback<T> {
  return (node: T) => {
    refs.forEach((ref) => {
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        if (ref && typeof ref !== 'function') {
          (ref as React.MutableRefObject<T>).current = node;
        }
      }
    });
  };
}

/**
 * Accessibly ensures an area of the page is visible,
 * both to sighted and screenreader users.
 * @param scrollTarget The element that will be scrolled to
 * @param focusTarget The element that will receive focus
 */

export function scrollIntoView(scrollTarget: React.RefObject<HTMLElement>, focusTarget: React.RefObject<HTMLElement>) {
  scrollTarget.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  focusTarget.current?.focus();
}

/**
 * For use in a try catch block for error handling.
 * Normalizes any error to a proper Error object.
 */
export function normalizeError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  if (isZoomExecutedFailure(error)) {
    return toZoomExecutedFailureError(error);
  }

  // Handle objects (but not null, since typeof null === 'object')
  if (error && typeof error === 'object') {
    try {
      return new Error(JSON.stringify(error));
    } catch {
      // Handle circular references or other JSON.stringify failures
      return new Error(`[Non-serializable object: ${error.constructor?.name || 'Object'}]`);
    }
  }

  return new Error(String(error));
}

export type WithRetryOptions = {
  retries?: number;
  /**
   * Initial delay in milliseconds before the first _retry_.
   */
  initialDelayMs?: number;
  /**
   * Backoff factor to apply to the delay between retries.
   */
  backoffFactor?: number;
  /**
   * Callback function to handle failures.
   */
  onFailure?: (args: {
    attempt: number;
    /**
     * Indicates if this is the final attempt
     */
    isFinalAttempt: boolean;
    error: Error;
  }) => void;
};

/**
 * Higher order function to add exponential backoff to an existing fn. Defaults
 * to one retry with no delay.
 */
export function withRetry<T, Args extends unknown[]>(
  fn: (...args: Args) => Promise<T>,
  { retries = 1, initialDelayMs = 0, backoffFactor = 1, onFailure: handleFailure }: WithRetryOptions = {},
) {
  return async function (...args: Args): Promise<T> {
    let delay = initialDelayMs;
    const maxAttempts = retries + 1;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await fn(...args);
      } catch (error) {
        const isFinalAttempt = attempt === maxAttempts;
        handleFailure?.({
          attempt,
          isFinalAttempt,
          error: normalizeError(error),
        });
        if (isFinalAttempt) {
          throw error;
        }
      }
      await new Promise((resolve) => setTimeout(resolve, delay));
      delay *= backoffFactor;
    }
    // Unreachable: the loop above always either returns or throws on the final
    // attempt. This is required so the inferred return type is Promise<T>
    // rather than Promise<T | undefined>.
    throw new Error('withRetry: exhausted attempts without returning or throwing');
  };
}

/**
 * Temporarily sets the given jotai atom before the operation, and safely
 * reverts it back to the previous value after the operation completes unless
 * the atom's value changed during the operation.
 */
export async function withTemporaryState<AtomType, ReturnType>({
  operation,
  jotaiStore,
  atom,
  value,
  onConflict: handleConflict,
}: {
  operation: () => ReturnType | Promise<ReturnType>;
  jotaiStore: JotaiStore;
  atom: PrimitiveAtom<AtomType>;
  value: AtomType;
  /**
   * Optional handler to resolve conflicts if the atom's value changed during
   * the operation. If not provided, the previous value will be restored.
   */
  onConflict?: (previousValue: AtomType, currentValue: AtomType) => AtomType;
}) {
  const previousValue = jotaiStore.get(atom);
  jotaiStore.set(atom, value);

  try {
    return await operation();
  } finally {
    // Revert to previous value unless the value changed
    const currentValue = jotaiStore.get(atom);
    jotaiStore.set(
      atom,
      jotaiStore.get(atom) === value || !handleConflict ? previousValue : handleConflict(currentValue, previousValue),
    );
  }
}

class TimeOutError extends Error {
  constructor(message?: string) {
    super(message);
    this.name = 'TimeOutError';
  }
}

/**
 * Wraps a promise and rejects it if it does not resolve within the given
 * timeout.
 */
export async function withTimeout<T>(
  fn: () => Promise<T>,
  timeoutMs: number,
  {
    timeoutMessage,
  }: {
    timeoutMessage?: string;
  } = {},
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      fn(),
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new TimeOutError(timeoutMessage)), timeoutMs);
      }),
    ]);
  } finally {
    if (timer !== undefined) {
      clearTimeout(timer);
    }
  }
}

export function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Maps a pronoun string from the backend to its localized translation. */
export function getLocalizedPronoun(t: TFunction, pronoun: string): string {
  const normalized = pronoun.toLowerCase().trim();
  switch (normalized) {
    case 'ce/cir':
      return t('telehealth-core:pronouns.cecir');
    case 'co/co':
      return t('telehealth-core:pronouns.coco');
    case 'cy/cyr':
      return t('telehealth-core:pronouns.cycyr');
    case 'ey/em':
      return t('telehealth-core:pronouns.eyem');
    case 'he/him':
      return t('telehealth-core:pronouns.hehim');
    case 'he/they':
      return t('telehealth-core:pronouns.hethey');
    case 'hey/hem':
      return t('telehealth-core:pronouns.heyhem');
    case 'ne/nem':
      return t('telehealth-core:pronouns.nenem');
    case 'qui/quem':
      return t('telehealth-core:pronouns.quiquem');
    case 'she/her':
      return t('telehealth-core:pronouns.sheher');
    case 'she/they':
      return t('telehealth-core:pronouns.shethey');
    case 'sie/hir':
      return t('telehealth-core:pronouns.siehir');
    case 'tey/tem':
      return t('telehealth-core:pronouns.teytem');
    case 'they/them':
      return t('telehealth-core:pronouns.theythem');
    case 've/vis':
      return t('telehealth-core:pronouns.vevis');
    case 'xe/xem':
      return t('telehealth-core:pronouns.xexem');
    case 'xie/hir':
      return t('telehealth-core:pronouns.xiehir');
    case 'yo/yo':
      return t('telehealth-core:pronouns.yoyo');
    case 'ze/zir':
      return t('telehealth-core:pronouns.zezir');
    default:
      return normalized;
  }
}

export class Stopwatch {
  private startTime: number;

  private constructor() {
    this.startTime = performance.now();
  }

  static start() {
    return new Stopwatch();
  }

  /** Returns elapsed time in milliseconds since the stopwatch was started. */
  elapsedMs() {
    return performance.now() - this.startTime;
  }

  /** Resets the stopwatch and returns the elapsed time in milliseconds. */
  stop() {
    const elapsed = this.elapsedMs();
    this.startTime = performance.now();
    return elapsed;
  }
}

/**
 * Awaits a promise and swallows its rejection, optionally passing the error to a handler.
 */
export async function trySafely<T>(promise: Promise<T>, onError: (e: Error) => void = noop) {
  try {
    return await promise;
  } catch (error) {
    const normalizedError = normalizeError(error);
    onError(normalizedError);
  }
}

/**
 * Returns an tuple where the first element is the given object with required
 * keys or `null` if any required keys are missing, and the second element is an
 * array of missing keys if any.
 */
export function requireKeys<T, K extends keyof T>(
  obj: Partial<T>,
  requiredKeys: Set<K>,
): [Partial<T> & Required<Pick<T, K>>] | [null, K[]] {
  const missingKeys = Array.from(requiredKeys).filter((key) => !(key in obj));
  if (missingKeys.length) {
    return [null, missingKeys];
  }
  return [obj as Required<Pick<T, K>> & Partial<T>];
}

/**
 * Returns a query string with only the specified keys and their values from the
 * given search string, or an empty string if any keys are missing.
 *
 * Does not decode/encode the query string in any way, perseving it exactly
 * except for filtering out keys that are not in the given set.
 *
 * e.g. "?foo=1&bar=2&qaz=3" with keys "foo" and "bar" would return
 * "?foo=1&bar=2"
 */
export function getRawQueryParams(search: string, keys: Set<string>): string {
  const query = search.startsWith('?') ? search.slice(1) : search;

  const queryParts = query.split('&');

  const queryString = queryParts.reduce((acc, part) => {
    const [key] = part.split('=');
    if (!keys.has(key)) {
      return acc;
    }

    const isFirstMatch = !acc;
    if (isFirstMatch) {
      return part;
    }

    return `${acc}&${part}`;
  }, '');

  return queryString && `?${queryString}`;
}

// Hack to check if it's the Arc browser
// https://stackoverflow.com/questions/76123232/can-javascript-detect-the-arc-browser
export function isArcBrowser() {
  return getComputedStyle(document.documentElement).getPropertyValue('--arc-palette-title') !== '';
}
